
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { format, parseISO, getDay } from 'date-fns';

export default {
    props: {
        restaurant: { type: Object, required: true },
    },
    computed: {
        ...mapGetters('order', ['isDelivery', 'address']),
        ...mapGetters('location', ['location']),
        name() {
            return this.restaurant.name;
        },
        restaurantLocations() {
            return this.restaurant.locations;
        },
        restaurantLocation() {
            const restaurantLocations = [...this.restaurantLocations];
            restaurantLocations.sort((a, b) => (a.distance < b.distance ? -1 : 1));
            if (this.isDelivery) {
                restaurantLocations.sort((a, b) =>
                    a.delivery_option && b.delivery_option && a.delivery_option.time < b.delivery_option.time ? -1 : 1
                );
            }
            let restaurantLocation;
            if (this.isDelivery) {
                restaurantLocation = restaurantLocations.find(
                    x => x.delivery_option && x.delivery_option.inside_radius
                );
            } else {
                restaurantLocation = restaurantLocations.find(x => x.pickup_option);
            }
            if (!restaurantLocation) return restaurantLocations[0];
            return restaurantLocation;
        },
        cover() {
            return this.restaurant.cover;
        },
        categories() {
            return this.restaurant.categories;
        },
        path() {
            return `/supplier/${this.restaurantLocation.slug}`;
        },
        closed() {
            return !this.restaurant.kitchen_is_open;
        },
        tempClosed() {
            return this.restaurant.temp_closed;
        },
        launchingSoon() {
            return this.restaurant.launching_soon;
        },
        deliveryClosed() {
            if (this.isDelivery) {
                if (Object.prototype.hasOwnProperty.call(this.restaurantLocation, 'delivery_option')) {
                    if (this.restaurantLocation.delivery_option.type === 'deliverit') {
                        if (this.restaurantLocation.delivery_option.closed) {
                            return true;
                        }
                    }
                }
            }
            return false;
        },
        deliveryClosedText() {
            if (this.isDelivery) {
                if (Object.prototype.hasOwnProperty.call(this.restaurantLocation, 'delivery_option')) {
                    if (this.restaurantLocation.delivery_option.type === 'deliverit') {
                        if (this.restaurantLocation.delivery_option.closed_text) {
                            return this.restaurantLocation.delivery_option.closed_text;
                        }
                    }
                }
            }
            return 'Levering lukket for i dag';
        },
        distanceStr() {
            return `${this.restaurant.distance.toString().replace('.', ',')} km`;
        },
        showDistance() {
            return this.restaurant.distance && this.location && !this.isDelivery;
        },
        showDeliveryTimeString() {
            return (
                this.restaurantLocation.delivery_option &&
                this.restaurantLocation.delivery_option.time > 0 &&
                (this.location || this.address) &&
                this.isDelivery
            );
        },
        deliveryTimeString() {
            const time = this.restaurantLocation.delivery_option?.time;
            if (!time) return null;
            return `${time}-${time + 5} min`;
        },
        publishedLocations() {
            return this.restaurant.published_locations;
        },
        isBakery() {
            return this.restaurant.is_bakery;
        },
        featuredDishes() {
            if (this.restaurant.locations.length) {
                return this.restaurant.locations[0].featured_dishes;
            }
            return [];
        },
    },
    methods: {
        ...mapMutations('order', ['setRestaurantLocation', 'setDeliveryOptionId']),
        ...mapActions('toast', ['setToast']),
        goToRestaurant() {
            if (this.restaurant.published_locations === 0) {
                return;
            }
            this.$emit('click');
            if (this.restaurantLocation && this.restaurantLocation.id) {
                this.setRestaurantLocation(this.restaurantLocation);
                // Delivery option is now automatically set in the store mutation
            }
            if (!this.restaurantLocation.slug && this.isDelivery && !this.address) {
                this.setToast({ msg: 'Vælg venligst en leveringsadresse', duration: 2 });
            } else if (this.restaurant.published_locations > 1) {
                this.$router.push(`/supplier-chain/${this.restaurant.slug}`);
            } else {
                this.$router.push(this.path);
            }
        },
        openHoursNextStr(date) {
            if (!date) {
                return 'Lukket';
            }

            // Check if today
            const dayOfWeekToday = getDay(Date.now());
            const dayOfWeekNextOpenHour = getDay(parseISO(this.restaurantLocation.open_hours_next));
            if (dayOfWeekToday === dayOfWeekNextOpenHour) {
                return `Åbner igen kl. ${format(parseISO(this.restaurantLocation.open_hours_next), 'HH:mm')}`;
            } else {
                const weekdayNames = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];
                if (
                    (dayOfWeekNextOpenHour === 0 && dayOfWeekToday === 6) ||
                    dayOfWeekNextOpenHour - 1 === dayOfWeekToday
                ) {
                    return `Åbner igen i morgen kl. ${format(
                        parseISO(this.restaurantLocation.open_hours_next),
                        'HH:mm'
                    )}`;
                }
                return `Åbner igen på ${weekdayNames[
                    dayOfWeekNextOpenHour !== 0 ? dayOfWeekNextOpenHour - 1 : 6
                ].toLowerCase()} kl. ${format(parseISO(this.restaurantLocation.open_hours_next), 'HH:mm')}`;
            }
        },
    },
};
